import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import UserListItem from './UserListItem';

const UserListPanel = ({
  pageTitle,
  searchQuery,
  onSearchChange,
  isLoading,
  filteredUsers,
  selectedUser,
  lastMessages,
  userDetails,
  onlineUsers,
  unreadCountByUserId,
  onSelectUser,
  isMobile,
  showChatPanel,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '300px', lg: '320px' },
        flexShrink: 0,
        display: {
          xs: isMobile && showChatPanel ? 'none' : 'flex',
          md: 'flex',
        },
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'text.titles',
            mb: 1.5,
            fontSize: '1.1rem',
          }}
        >
          {pageTitle}
        </Typography>
        <TextField
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: isDark
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.grey[500], 0.08),
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': {
                border: `1px solid ${theme.palette.primary.main}`,
              },
              fontSize: '0.85rem',
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1, pb: 1 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : filteredUsers?.length === 0 ? (
          <Box sx={{ px: 2, pt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {searchQuery ? 'No results found' : 'No paired users yet'}
            </Typography>
          </Box>
        ) : (
          filteredUsers?.map((user) => (
            <UserListItem
              key={user._id}
              user={user}
              isSelected={selectedUser?._id === user._id}
              onClick={() => onSelectUser(user)}
              lastMessage={lastMessages[user._id]}
              userDetails={userDetails}
              isOnline={onlineUsers.has(user._id)}
              unreadCount={unreadCountByUserId[user._id] || 0}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default UserListPanel;
